import { urlForLog } from "./oauth-url-redaction";

// Shape gate: https:// then a plain host ending in mindstone.com or
// mindstone.dev, then a path or nothing. This alone refuses userinfo, ports,
// backslashes and trailing dots, and pins the host substring to exactly what
// the URL parser will navigate to.
const regex = /^https:\/\/([a-zA-Z0-9_-]+\.)*mindstone\.(com|dev)(\/.*)?$/;

// Host policy (FOX-4690). The suffix match let any depth of subdomain and the
// apex through, so a dangling or third-party DNS record anywhere under the
// domain would receive the post-login navigation while the browser carries the
// .mindstone.com auth cookies. An exact list is not possible: every tenant
// space is <slug>.<parent> (gateway/src/index.ts routes the wildcard to
// web.space) and spaces send signed-out users here with their own host. So a
// host must be exactly one DNS label under an environment parent — every
// product host and every space fits — or one of the deeper first-party hosts.
// Residual: a single-label record pointing off-platform is still accepted
// unless its label is reserved below; the repo has no DNS zone listing to
// derive that set from.
/* eslint-disable lingui/no-unlocalized-strings -- hostnames, not UI copy */
const ENVIRONMENT_PARENTS = ["mindstone.com", "s101.mindstone.dev", "local.mindstone.dev"];
const DEEP_FIRST_PARTY_HOSTS = new Set([
  "admin.internal.mindstone.com",
  "admin.internal.s101.mindstone.dev",
  "admin.internal.local.mindstone.dev",
]);
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
// Labels commonly served by third parties (marketing site, mail/link tracking,
// help desk, status page), never a Mindstone app or a space.
const RESERVED_LABELS = new Set([
  "www", "mail", "email", "em", "links", "click", "go", "blog", "help", "support", "status",
]);
/* eslint-enable lingui/no-unlocalized-strings */

function isAllowedHost(hostname: string): boolean {
  if (DEEP_FIRST_PARTY_HOSTS.has(hostname)) return true;
  for (const parent of ENVIRONMENT_PARENTS) {
    if (!hostname.endsWith(`.${parent}`)) continue;
    const label = hostname.slice(0, -parent.length - 1);
    return DNS_LABEL.test(label) && !RESERVED_LABELS.has(label);
  }
  return false;
}

function isAllowedRedirect(url: string): boolean {
  if (!regex.test(url)) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return (
    parsed.protocol === "https:" &&
    parsed.username === "" &&
    parsed.password === "" &&
    parsed.port === "" &&
    isAllowedHost(parsed.hostname)
  );
}

// URLSearchParams already removes the query's encoding layer. Preserve URL
// query values; decode only legacy callers that encoded the whole URL twice.
// This normalizes transport only: callers still apply their redirect policy.
export function decodeRedirectUrl(value: string): string {
  if (/^(?:https?:\/\/|\/)/i.test(value)) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed escape (e.g. `%E0%A4%A`) is not a destination; leave it as
    // is so the caller's policy rejects it instead of throwing.
    return value;
  }
}

// The log lines carry origin + pathname only: a redirect URL's query can hold
// an MCP OAuth consent `txn` (tmcp U1, Codex r1 G-F1) or any other one-time
// value. The return value is unchanged.
export function sanitizeRedirectURl(redirectUrl: string) {
  const url = decodeRedirectUrl(redirectUrl);
  if (isAllowedRedirect(url)) {
    console.log("URL Valid", urlForLog(url));
    return url;
  }
  console.log("URL Invalid", urlForLog(url));
  return "/";
}

// For the places that also accept an in-app path (a signed-in visitor bounced
// off /login, root's authed redirect): a same-origin path, or an absolute URL
// that passes the allowlist above; null otherwise. A path must stay on this
// origin when a browser resolves it: browsers strip tabs and newlines while
// parsing, so `/\t/host` becomes the protocol-relative `//host`. `//` and
// `/\` prefixes, control characters and spaces are refused outright (the
// explicit prefix check also stops `//placeholder.invalid` matching the base
// below), then the path is resolved against a placeholder origin that must
// survive.
const PATH_BASE = "https://placeholder.invalid";
export function safeRedirectTarget(raw: string): string | null {
  const url = decodeRedirectUrl(raw);
  if (url.startsWith("/")) {
    if (/^\/[/\\]/.test(url) || /[\x00-\x20\x7f]/.test(url)) return null;
    try {
      return new URL(url, PATH_BASE).origin === PATH_BASE ? url : null;
    } catch {
      return null;
    }
  }
  return isAllowedRedirect(url) ? url : null;
}
